import React from "react";
import { Link } from "react-router-dom";
import { Share, PlusSquare, Smartphone, Apple, Download, ArrowLeft } from "lucide-react";
import { isIOS, isStandalone } from "@/lib/native";

const Step = ({ n, children }) => (
  <li className="flex items-start gap-3">
    <span className="w-6 h-6 shrink-0 grid place-items-center bg-zinc-900 text-white text-xs font-bold">{n}</span>
    <span className="text-sm text-zinc-700 leading-6">{children}</span>
  </li>
);

export default function Install() {
  const ios = isIOS();
  if (isStandalone()) {
    return (
      <div className="min-h-dvh bg-zinc-50 grid place-items-center p-6">
        <div className="text-center" data-testid="install-already">
          <Smartphone size={28} className="mx-auto mb-3" />
          <h1 className="font-heading font-black text-2xl">You're using the app</h1>
          <p className="text-sm text-zinc-500 mt-1">Mind Maker 369 is already installed on this device.</p>
          <Link to="/" className="inline-block mt-5 text-xs font-semibold underline decoration-dotted">Go to dashboard</Link>
        </div>
      </div>
    );
  }
  return (
    <div className="min-h-dvh bg-zinc-50 safe-top safe-bottom">
      <div className="max-w-xl mx-auto px-5 py-8">
        <Link to="/login" data-testid="install-back" className="text-xs font-semibold text-zinc-500 inline-flex items-center gap-1 hover:text-zinc-900"><ArrowLeft size={13} /> Back</Link>
        <div className="mt-6 flex items-center gap-4">
          <img src="/icon-192.png" alt="" className="w-16 h-16 rounded-2xl shadow" />
          <div>
            <div className="text-[11px] tracking-[0.2em] uppercase text-zinc-500 font-semibold">Install</div>
            <h1 data-testid="install-title" className="font-heading font-black tracking-tight text-3xl">Mind Maker 369 on your phone</h1>
          </div>
        </div>
        <p className="mt-4 text-sm text-zinc-600">Full-screen, one-tap access to billing, Khata, tasks and the GST vault — with offline support and push reminders.</p>

        <section className={`mt-8 bg-white border p-5 ${ios ? "border-zinc-900" : "border-zinc-200"}`} data-testid="install-ios">
          <h2 className="font-heading font-bold text-lg flex items-center gap-2"><Apple size={18} /> iPhone &amp; iPad (Safari)</h2>
          <ol className="mt-4 space-y-3">
            <Step n={1}>Open this page in <b>Safari</b> (Chrome on iOS can't install apps).</Step>
            <Step n={2}>Tap the <Share size={14} className="inline -mt-0.5" /> <b>Share</b> button in the bottom toolbar.</Step>
            <Step n={3}>Scroll and choose <PlusSquare size={14} className="inline -mt-0.5" /> <b>Add to Home Screen</b>, then tap <b>Add</b>.</Step>
            <Step n={4}>Launch <b>Mind Maker</b> from your home screen — sign in once and stay signed in.</Step>
          </ol>
          <p className="mt-4 text-xs text-zinc-500">Requires iOS 16.4+ for push notifications. App Store version coming soon.</p>
        </section>

        <section className={`mt-4 bg-white border p-5 ${!ios ? "border-zinc-900" : "border-zinc-200"}`} data-testid="install-android">
          <h2 className="font-heading font-bold text-lg flex items-center gap-2"><Download size={18} /> Android (Chrome)</h2>
          <ol className="mt-4 space-y-3">
            <Step n={1}>Tap the <b>⋮</b> menu in Chrome.</Step>
            <Step n={2}>Choose <b>Install app</b> (or <b>Add to Home screen</b>).</Step>
          </ol>
        </section>
      </div>
    </div>
  );
}
